import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Select as SelectAntd } from 'antd';
import moment from 'moment';
import { DatePicker, Input, Popup, RichArea, Select } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { dateFormatter } from 'helpers/dateFormatter';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { RULES } from '_organisms/Offer/constants';
import { getOpeningsList } from 'actions';
import type { Opening } from 'store/reducers/jobReducer';
import './style.scss';

interface OpeningOption {
  uuid?: string;
  value: string;
  label: string;
  offices: Array<{ value: string; label: string }>;
  expected?: string;
  current?: boolean;
}

const openingsMapper = (openings: Opening[]): OpeningOption[] =>
  openings.map(o => ({
    value: o.uuid,
    label: `${o.level} - ${o.team_lead.full_name}`,
    offices: (o.offices ?? []).map(office => ({ value: office.uuid, label: office.name })),
    expected: o.expected,
  }));

interface PopupEditHiredData {
  uuid: string;
  job_assignment: string;
  candidate: { uuid: string; fullNameLatin?: string; fullNameLocal?: string };
  team_lead: string;
  location: { value: string; label: string };
  opening: {
    uuid?: string;
    level?: string;
    expected?: string;
    offices: Array<{ value: string; label: string }>;
  };
  entry_date: string;
  probation_period?: number | string;
  comment?: string;
}

interface PopupEditHiredSaveBody {
  opening_id: string;
  location_id: string;
  entry_date: string;
  probation_period: number;
  comment?: string;
}

interface PopupEditHiredProps {
  open: boolean;
  data: PopupEditHiredData | null;
  jobName?: string;
  loading?: boolean;
  onClose: () => void;
  onSave: (jobAssignmentId: string, body: PopupEditHiredSaveBody) => void;
}

export const PopupEditHired: React.FC<PopupEditHiredProps> = ({ open, data, jobName, loading, onClose, onSave }) => {
  const { t } = useTranslation();
  const { id: jobId } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [openings, setOpenings] = useState<OpeningOption[]>([]);

  useEffect(() => {
    if (open && data) {
      const opening: OpeningOption = {
        current: true,
        value: data.opening.uuid as string,
        label: `${data.opening.level} - ${data.team_lead}`,
        offices: data.opening.offices,
        expected: data.opening.expected,
      };
      form.setFieldsValue({
        opening,
        location: data.location,
        entry_date: moment(data.entry_date, 'DD.MM.YYYY'),
        probation_period: data.probation_period,
        comment: '',
      });
      getOpeningsList(jobId, { status: 'Open' })
        .then(ops => setOpenings([opening, ...openingsMapper(ops ?? []).filter(o => o.value !== opening.value)]))
        .catch(() => setOpenings([]));
    } else {
      form.setFieldsValue({
        opening: undefined,
        location: undefined,
        entry_date: undefined,
        probation_period: '',
        comment: '',
      });
      setOpenings([]);
    }
  }, [open]);

  const onSaveForm = ({
    opening,
    location,
    entry_date,
    probation_period,
    comment,
  }: {
    opening: { value: string };
    location: { value: string };
    entry_date: moment.Moment;
    probation_period: number | string;
    comment?: string;
  }) => {
    if (!data) return;
    onSave(data.job_assignment, {
      opening_id: opening.value,
      location_id: location.value,
      entry_date: entry_date.format('YYYY-MM-DD'),
      probation_period: +probation_period,
      comment,
    });
  };

  if (!data) return null;
  return (
    <Popup
      open={open}
      loading={loading}
      className="popup-edit-hired mobile-full-size"
      title={t('translation:popup_edit_hired.title')}
      cancelButtonProps={{
        children: t('translation:popup_edit_hired.button_cancel'),
        onClick: onClose,
      }}
      okButtonProps={{
        children: t('translation:popup_edit_hired.button_save'),
        form: 'form-edit-hired',
        htmlType: 'submit',
      }}
    >
      <div className="candidate-info">
        <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>{data.candidate.fullNameLatin}</Typography.Text>
        <Typography.Label weight={TYPOGRAPHY_WEIGHT.NORMAL}>{data.candidate.fullNameLocal}</Typography.Label>
        <Typography.Label weight={TYPOGRAPHY_WEIGHT.NORMAL}>{jobName}</Typography.Label>
      </div>
      <Form id="form-edit-hired" form={form} onFinish={onSaveForm} validateTrigger="onSubmit">
        <Select
          labelInValue
          label={t('translation:popup_edit_hired.label_opening')}
          itemProps={{ name: 'opening', rules: [REQUIRED_RULE] }}
          onChange={(opening: OpeningOption) => {
            const location = opening.offices.length === 1 ? opening.offices[0] : undefined;
            form.setFieldsValue({ opening, location });
          }}
        >
          {openings.map(opening => (
            <SelectAntd.Option key={opening.uuid} value={opening.value} label={opening.label} offices={opening.offices}>
              <div className="opening-select-option">
                <Typography.Text>
                  {opening.label}
                  {opening.current && <span> {t('translation:popup_edit_hired.text_current')}</span>}
                </Typography.Text>
                <Typography.Caption>
                  {t('translation:popup_edit_hired.text_expected_date', {
                    date: dateFormatter(opening.expected),
                  } as Record<string, unknown>)}
                </Typography.Caption>
                <Typography.Caption>{opening.offices.map(o => o.label).join(', ')}</Typography.Caption>
              </div>
            </SelectAntd.Option>
          ))}
        </Select>
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.opening !== curr.opening}>
          {({ getFieldValue }) => (
            <Select
              labelInValue
              label={t('translation:popup_edit_hired.label_location')}
              options={getFieldValue('opening')?.offices}
              disabled={getFieldValue('opening')?.offices.length === 1}
              itemProps={{ name: 'location', rules: [REQUIRED_RULE] }}
            />
          )}
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <DatePicker
              label={t('translation:popup_edit_hired.label_entry_date')}
              itemProps={{ name: 'entry_date', rules: [REQUIRED_RULE] }}
            />
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Input
              label={t('translation:popup_edit_hired.label_probation_period')}
              placeholder={t('translation:popup_edit_hired.placeholder_probation_period')}
              itemProps={{ name: 'probation_period', rules: RULES.PERIOD }}
            />
          </Col>
          <Col span={12}>
            <Typography.Text className="months">{t('translation:popup_edit_hired.text_months')}</Typography.Text>
          </Col>
        </Row>
        <Form.Item noStyle name="comment">
          <RichArea
            label={t('translation:popup_edit_hired.label_comment')}
            placeholder={t('translation:popup_edit_hired.placeholder_comment')}
          />
        </Form.Item>
      </Form>
    </Popup>
  );
};
